import { AnimatePresence, motion } from 'motion/react';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Debt } from '../../lib/types';

interface DebtPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  debt: Debt | null;
  onConfirm: (debtId: string, amount: number, date: string) => Promise<void>;
}

export function DebtPaymentModal({ isOpen, onClose, debt, onConfirm }: DebtPaymentModalProps) {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setDate(new Date().toISOString().split('T')[0]);
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    if (!debt || !amount) return;
    const paymentAmount = parseFloat(amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) return;

    await onConfirm(debt.id, paymentAmount, date);
  };

  if (!debt) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm" />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-sm bg-white dark:bg-black border border-zinc-100 dark:border-zinc-900 rounded-[32px] shadow-2xl overflow-hidden transition-colors"
          >
            <div className="p-8 space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Pay Debt</h2>
                  <p className="text-xs text-zinc-500 font-medium">Record a payment for "{debt.name}"</p>
                </div>
                <button onClick={onClose} className="w-10 h-10 bg-zinc-50 dark:bg-zinc-900 rounded-2xl flex items-center justify-center text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4 pt-2">
                <div className="space-y-1.5"><label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Payment Amount</label><input type="number" step="0.01" placeholder="0.00" className="w-full px-5 py-4 bg-zinc-50 dark:bg-zinc-950 dark:text-zinc-100 border border-zinc-100 dark:border-zinc-900 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-bold text-lg" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
                <div className="space-y-1.5"><label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Payment Date</label><input type="date" className="w-full px-5 py-4 bg-zinc-50 dark:bg-zinc-950 dark:text-zinc-100 border border-zinc-100 dark:border-zinc-900 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all" value={date} onChange={(e) => setDate(e.target.value)} /></div>
                <button onClick={handleConfirm} className="w-full py-5 bg-zinc-900 dark:bg-zinc-50 dark:text-zinc-900 text-white rounded-[24px] font-bold shadow-sm shadow-zinc-200 active:scale-95 transition-transform">Confirm Payment</button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}